import { useState } from 'react'
import Avatar from '../components/Avatar.jsx'
import SyncChip from '../components/SyncChip.jsx'
import { calendarSyncState } from '../lib/google.js'
import { fmtDate, dateInputToDate, dateToInput, tsToDate } from '../lib/format.js'

const PROFILE_FIELDS = [
  ['role', 'Role / Company'], ['phone', 'Phone'], ['altEmail', 'Alternate email'],
  ['timeZone', 'Time zone'], ['notes', 'Notes (best times, preferences)'],
]

export default function TeamView({ users = [], dates = [], currentUid, onSaveProfile, onAddDate, onSyncDate, onUpdateDate, onDeleteDate, googleCalendarId }) {
  const [editing, setEditing] = useState(null) // null | {…patch}
  const [addingDate, setAddingDate] = useState(null) // null | { who, label, start, note }
  const [editingDate, setEditingDate] = useState(null) // null | {id, who, label, start, note}

  const startEdit = (u) => setEditing({ role: u.role || '', phone: u.phone || '', altEmail: u.altEmail || '', timeZone: u.timeZone || '', notes: u.notes || '' })
  const saveEdit = () => { onSaveProfile(editing); setEditing(null) }

  const saveDate = () => {
    if (!addingDate?.start) return
    onAddDate({ who: addingDate.who, label: addingDate.label, start: dateInputToDate(addingDate.start), note: addingDate.note })
    setAddingDate(null)
  }

  const saveEditDate = () => {
    if (!editingDate?.start) return
    onUpdateDate(editingDate.id, { who: editingDate.who, label: editingDate.label, start: dateInputToDate(editingDate.start), note: editingDate.note })
    setEditingDate(null)
  }

  return (
    <>
      <div className="eyebrow" style={{ marginBottom: 12 }}><span className="tag-label" style={{ color: 'var(--color-gold)' }}>Hub</span></div>
      <h1 className="font-display" style={{ fontSize: 42, margin: '0 0 4px', lineHeight: 0.95, textTransform: 'uppercase', color: 'var(--color-white)' }}>Team</h1>
      <p style={{ margin: '0 0 22px', fontSize: 13, color: 'var(--color-muted)' }}>The Career Nomad team — contacts and who's out.</p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 12, marginBottom: 30 }}>
        {users.map((u) => (
          <div key={u.uid} style={{ padding: 14, background: 'var(--color-panel-dark)', border: '1px solid var(--color-divider-dark)', borderRadius: 'var(--radius-sm)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Avatar user={u} size={34} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div title={u.name} style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--color-white)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.name}</div>
                <div title={u.role || ''} style={{ fontSize: 12, color: 'var(--color-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.role || '—'}</div>
              </div>
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-body)', lineHeight: 1.7 }}>
              <div>{u.email}</div>
              {u.phone && <div>{u.phone}{u.timeZone ? ` · ${u.timeZone}` : ''}</div>}
              {u.notes && <div style={{ color: 'var(--color-muted)' }}>{u.notes}</div>}
            </div>
            {u.uid === currentUid && !editing && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <button onClick={() => startEdit(u)} style={{ background: 'none', border: 'none', color: 'var(--color-muted)', cursor: 'pointer', fontSize: 12 }}>Edit</button>
              </div>
            )}
          </div>
        ))}
      </div>

      {editing && (
        <div style={{ padding: 14, marginBottom: 24, background: 'var(--color-panel-dark)', border: '1px solid var(--color-gold)', borderRadius: 'var(--radius-sm)' }}>
          <h2 className="tag-label" style={{ color: 'var(--color-gold)', marginBottom: 10 }}>Edit my card</h2>
          <div style={{ display: 'grid', gap: 8 }}>
            {PROFILE_FIELDS.map(([k, ph]) => (
              <input key={k} value={editing[k]} placeholder={ph} onChange={(e) => setEditing((s) => ({ ...s, [k]: e.target.value }))}
                style={{ background: 'var(--color-bg-dark)', border: '1px solid var(--color-divider-dark)', borderRadius: 'var(--radius-sm)', padding: '7px 9px', color: 'var(--color-white)', fontSize: 12.5 }} />
            ))}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setEditing(null)} className="btn" style={{ fontSize: 12, padding: '7px 12px' }}>Cancel</button>
              <button onClick={saveEdit} className="btn btn-gold" style={{ fontSize: 12, padding: '7px 12px' }}>Save</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <h2 className="tag-label" style={{ color: 'var(--color-muted)' }}>Dates to Remember</h2>
        <button onClick={() => setAddingDate({ who: '', label: '', start: '', note: '' })} style={{ background: 'none', border: 'none', color: 'var(--color-muted)', cursor: 'pointer', fontSize: 12 }}>+ Add</button>
      </div>

      {addingDate && (
        <div style={{ padding: 14, marginBottom: 16, background: 'var(--color-panel-dark)', border: '1px solid var(--color-gold)', borderRadius: 'var(--radius-sm)' }}>
          <h2 className="tag-label" style={{ color: 'var(--color-gold)', marginBottom: 10 }}>New Date</h2>
          <div style={{ display: 'grid', gap: 8 }}>
            {[['who', 'Who'], ['label', 'Label']].map(([k, ph]) => (
              <input key={k} value={addingDate[k]} placeholder={ph} onChange={(e) => setAddingDate((s) => ({ ...s, [k]: e.target.value }))}
                style={{ background: 'var(--color-bg-dark)', border: '1px solid var(--color-divider-dark)', borderRadius: 'var(--radius-sm)', padding: '7px 9px', color: 'var(--color-white)', fontSize: 12.5 }} />
            ))}
            <input type="date" value={addingDate.start} onChange={(e) => setAddingDate((s) => ({ ...s, start: e.target.value }))}
              style={{ background: 'var(--color-bg-dark)', border: '1px solid var(--color-divider-dark)', borderRadius: 'var(--radius-sm)', padding: '7px 9px', color: 'var(--color-white)', fontSize: 12.5 }} />
            <input value={addingDate.note} placeholder="Note" onChange={(e) => setAddingDate((s) => ({ ...s, note: e.target.value }))}
              style={{ background: 'var(--color-bg-dark)', border: '1px solid var(--color-divider-dark)', borderRadius: 'var(--radius-sm)', padding: '7px 9px', color: 'var(--color-white)', fontSize: 12.5 }} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setAddingDate(null)} className="btn" style={{ fontSize: 12, padding: '7px 12px' }}>Cancel</button>
              <button onClick={saveDate} className="btn btn-gold" style={{ fontSize: 12, padding: '7px 12px' }}>Save</button>
            </div>
          </div>
        </div>
      )}

      {editingDate && (
        <div style={{ padding: 14, marginBottom: 16, background: 'var(--color-panel-dark)', border: '1px solid var(--color-gold)', borderRadius: 'var(--radius-sm)' }}>
          <h2 className="tag-label" style={{ color: 'var(--color-gold)', marginBottom: 10 }}>Edit Date</h2>
          <div style={{ display: 'grid', gap: 8 }}>
            {[['who', 'Who'], ['label', 'Label']].map(([k, ph]) => (
              <input key={k} value={editingDate[k]} placeholder={ph} onChange={(e) => setEditingDate((s) => ({ ...s, [k]: e.target.value }))}
                style={{ background: 'var(--color-bg-dark)', border: '1px solid var(--color-divider-dark)', borderRadius: 'var(--radius-sm)', padding: '7px 9px', color: 'var(--color-white)', fontSize: 12.5 }} />
            ))}
            <input type="date" value={editingDate.start} onChange={(e) => setEditingDate((s) => ({ ...s, start: e.target.value }))}
              style={{ background: 'var(--color-bg-dark)', border: '1px solid var(--color-divider-dark)', borderRadius: 'var(--radius-sm)', padding: '7px 9px', color: 'var(--color-white)', fontSize: 12.5 }} />
            <input value={editingDate.note} placeholder="Note" onChange={(e) => setEditingDate((s) => ({ ...s, note: e.target.value }))}
              style={{ background: 'var(--color-bg-dark)', border: '1px solid var(--color-divider-dark)', borderRadius: 'var(--radius-sm)', padding: '7px 9px', color: 'var(--color-white)', fontSize: 12.5 }} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setEditingDate(null)} className="btn" style={{ fontSize: 12, padding: '7px 12px' }}>Cancel</button>
              <button onClick={saveEditDate} className="btn btn-gold" style={{ fontSize: 12, padding: '7px 12px' }}>Save</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ border: '1px solid var(--color-divider-dark)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
        {dates.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-dim)', padding: '10px 12px' }}>No upcoming dates.</div>}
        {dates.map((d) => (
          <div key={d.id} style={{ display: 'flex', gap: 12, padding: '10px 12px', borderTop: '1px solid var(--color-divider-dark)', fontSize: 12.5, color: 'var(--text-body)' }}>
            <span className="font-mono" style={{ color: 'var(--color-gold)', minWidth: 90 }}>{d.start ? fmtDate(tsToDate(d.start)) : '—'}</span>
            <span title={d.label} style={{ fontWeight: 600, color: 'var(--color-white)', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 1 }}>{d.label}</span>
            <span title={`${d.who}${d.note ? ` · ${d.note}` : ''}`} style={{ color: 'var(--color-muted)', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 1 }}>{d.who}{d.note ? ` · ${d.note}` : ''}</span>
            <span style={{ flex: 1 }} />
            <SyncChip state={calendarSyncState(d)} onSync={() => onSyncDate && onSyncDate(d)} />
            <button onClick={() => setEditingDate({ id: d.id, who: d.who || '', label: d.label || '', start: dateToInput(tsToDate(d.start)) || '', note: d.note || '' })} title="Edit" style={{ background: 'none', border: 'none', color: 'var(--color-muted)', cursor: 'pointer', fontSize: 12 }}>✎</button>
            <button onClick={() => onDeleteDate && onDeleteDate(d)} title="Delete" style={{ background: 'none', border: 'none', color: 'var(--color-muted)', cursor: 'pointer', fontSize: 12 }}>✕</button>
          </div>
        ))}
      </div>
    </>
  )
}
